/**
 * Line icons, 1.5 px stroke on a 20-unit grid, drawn for FRICTION (no icon font / dependency).
 * Colour follows `currentColor`.
 */
import type { CSSProperties, ReactNode } from 'react';

const PATHS = {
  pause: (
    <>
      <path d="M7 4.5v11M13 4.5v11" />
    </>
  ),
  play: <path d="M6.5 4.5v11l9-5.5z" />,
  factory: (
    <>
      <path d="M2.5 16.5h15M3.5 16.5V9l4 2.5V9l4 2.5V9l4 2.5v5" />
      <path d="M13.5 9V3.5h2.5V10" />
    </>
  ),
  manpower: (
    <>
      <path d="M4 11.5c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
      <path d="M2.5 11.5h15M10 3.5v2.5" />
      <path d="M6.5 11.5v2.5a3.5 3.5 0 0 0 7 0v-2.5" />
    </>
  ),
  research: (
    <>
      <path d="M8 3h4M8.5 3v5l-4.5 8a1 1 0 0 0 .9 1.5h10.2a1 1 0 0 0 .9-1.5l-4.5-8V3" />
      <path d="M6 13h8" />
    </>
  ),
  star: <path d="M10 3.2l2 4.3 4.6.5-3.4 3.2 1 4.6L10 13.5l-4.2 2.3 1-4.6L3.4 8l4.6-.5z" />,
  clock: (
    <>
      <circle cx="10" cy="10" r="6.8" />
      <path d="M10 6v4.2l2.8 1.8" />
    </>
  ),
  sun: (
    <>
      <circle cx="10" cy="10" r="3.2" />
      <path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" />
    </>
  ),
  moon: <path d="M15.5 12.6A6.5 6.5 0 0 1 7.4 4.5a6.5 6.5 0 1 0 8.1 8.1z" />,
  cloud: <path d="M6 15h8.6a3.1 3.1 0 0 0 .3-6.2 4.7 4.7 0 0 0-9-1.1A3.7 3.7 0 0 0 6 15z" />,
  rain: (
    <>
      <path d="M6 11.5h8.6a2.9 2.9 0 0 0 .3-5.8 4.4 4.4 0 0 0-8.4-1A3.4 3.4 0 0 0 6 11.5z" />
      <path d="M7 14l-1 2.8M10.5 14l-1 2.8M14 14l-1 2.8" />
    </>
  ),
  storm: (
    <>
      <path d="M6 11.5h8.6a2.9 2.9 0 0 0 .3-5.8 4.4 4.4 0 0 0-8.4-1A3.4 3.4 0 0 0 6 11.5z" />
      <path d="M11 12.5l-2 3h3l-2 3" />
    </>
  ),
  snow: (
    <>
      <path d="M6 11.5h8.6a2.9 2.9 0 0 0 .3-5.8 4.4 4.4 0 0 0-8.4-1A3.4 3.4 0 0 0 6 11.5z" />
      <path d="M7 14v2.6M5.8 15.3h2.4M13 14v2.6M11.8 15.3h2.4M10 15.5v2.4M8.8 16.7h2.4" />
    </>
  ),
  fog: (
    <>
      <path d="M6 9.5h8.6a2.6 2.6 0 0 0 .2-5.2 4 4 0 0 0-7.6-.9A3 3 0 0 0 6 9.5z" />
      <path d="M3 12.5h14M4.5 15h11M6 17.5h8" />
    </>
  ),
  mud: (
    <>
      <path d="M2.5 11.5c1.6-1.2 3-1.2 4.6 0s3 1.2 4.6 0 3-1.2 4.6 0" />
      <path d="M2.5 15.5c1.6-1.2 3-1.2 4.6 0s3 1.2 4.6 0 3-1.2 4.6 0" />
      <path d="M6 7.5l1.5-3M11 7.5l1.5-3" />
    </>
  ),
  frost: (
    <>
      <path d="M10 2.5v15M3.5 6.2l13 7.6M3.5 13.8l13-7.6" />
      <path d="M8.2 3.8L10 5.5l1.8-1.7M8.2 16.2L10 14.5l1.8 1.7" />
    </>
  ),
  courier: (
    <>
      <rect x="3" y="5.5" width="14" height="9.5" rx="1" />
      <path d="M3.5 6l6.5 5 6.5-5" />
    </>
  ),
  radio: (
    <>
      <path d="M10 10v7.5M7.5 17.5h5" />
      <circle cx="10" cy="8.5" r="1.3" />
      <path d="M6.6 5.2a4.8 4.8 0 0 0 0 6.6M13.4 5.2a4.8 4.8 0 0 1 0 6.6M4.3 3a8 8 0 0 0 0 11M15.7 3a8 8 0 0 1 0 11" />
    </>
  ),
  telephone: (
    <path d="M5 3.5h3l1.2 3.2-1.8 1.2a9 9 0 0 0 4.7 4.7l1.2-1.8 3.2 1.2v3a1.5 1.5 0 0 1-1.6 1.5C8.6 16 4 11.4 3.5 5.1A1.5 1.5 0 0 1 5 3.5z" />
  ),
  move: (
    <>
      <path d="M3 14c3.5-6 7.5-8 13-8" />
      <path d="M12.5 3l3.5 3-3.5 3" />
    </>
  ),
  attack: (
    <>
      <path d="M3.5 16.5l10-10M4.5 11.5l4 4M13.5 3.5h3v3" />
      <path d="M16.5 3.5l-3 3" />
    </>
  ),
  hold: (
    <>
      <path d="M10 2.8l6 2.2v4.6c0 3.6-2.5 6.2-6 7.6-3.5-1.4-6-4-6-7.6V5z" />
      <path d="M7 10h6" />
    </>
  ),
  retreat: (
    <>
      <path d="M17 14c-3.5-6-7.5-8-13-8" />
      <path d="M7.5 3L4 6l3.5 3" />
    </>
  ),
  lock: (
    <>
      <rect x="4.5" y="9" width="11" height="8" rx="1" />
      <path d="M7 9V6.5a3 3 0 0 1 6 0V9" />
    </>
  ),
  check: <path d="M4 10.5l4 4 8-9" />,
  close: <path d="M5 5l10 10M15 5L5 15" />,
  copy: (
    <>
      <rect x="7" y="7" width="9.5" height="9.5" rx="1" />
      <path d="M13 7V4.5a1 1 0 0 0-1-1H4.5a1 1 0 0 0-1 1V12a1 1 0 0 0 1 1H7" />
    </>
  ),
  refresh: (
    <>
      <path d="M16 10a6 6 0 1 1-1.8-4.3" />
      <path d="M16 3.5v3.8h-3.8" />
    </>
  ),
  dice: (
    <>
      <rect x="3.5" y="3.5" width="13" height="13" rx="2" />
      <circle cx="7" cy="7" r="0.9" fill="currentColor" />
      <circle cx="13" cy="13" r="0.9" fill="currentColor" />
      <circle cx="10" cy="10" r="0.9" fill="currentColor" />
    </>
  ),
  menu: <path d="M3.5 6h13M3.5 10h13M3.5 14h13" />,
  focus: (
    <>
      <circle cx="10" cy="10" r="5.5" />
      <path d="M10 2v4M10 14v4M2 10h4M14 10h4" />
    </>
  ),
  eye: (
    <>
      <path d="M2 10s3-5.5 8-5.5 8 5.5 8 5.5-3 5.5-8 5.5S2 10 2 10z" />
      <circle cx="10" cy="10" r="2.3" />
    </>
  ),
  intel: (
    <>
      <rect x="3.5" y="3" width="13" height="14" rx="1" />
      <path d="M6.5 7h7M6.5 10h7M6.5 13h4" />
    </>
  ),
  gear: (
    <>
      <circle cx="10" cy="10" r="2.6" />
      <path d="M10 2.5v2.2M10 15.3v2.2M2.5 10h2.2M15.3 10h2.2M4.7 4.7l1.6 1.6M13.7 13.7l1.6 1.6M4.7 15.3l1.6-1.6M13.7 6.3l1.6-1.6" />
    </>
  ),
  chevronRight: <path d="M8 4.5l5.5 5.5L8 15.5" />,
  chevronDown: <path d="M4.5 8l5.5 5.5L15.5 8" />,
  chevronLeft: <path d="M12 4.5L6.5 10l5.5 5.5" />,
  users: (
    <>
      <circle cx="7.5" cy="7" r="2.8" />
      <path d="M2.5 16.5c.4-3 2.4-4.8 5-4.8s4.6 1.8 5 4.8" />
      <path d="M12.5 4.4a2.8 2.8 0 0 1 0 5.2M14.5 12c1.6.6 2.7 2.2 3 4.5" />
    </>
  ),
  signal: (
    <>
      <path d="M4 16v-2.5M8 16v-5M12 16V8M16 16V4.5" />
    </>
  ),
  map: (
    <>
      <path d="M2.5 5l5-2 5 2 5-2v12l-5 2-5-2-5 2z" />
      <path d="M7.5 3v12M12.5 5v12" />
    </>
  ),
  flag: (
    <>
      <path d="M4.5 17.5V3" />
      <path d="M4.5 3.5h10l-2.5 3.5 2.5 3.5h-10" />
    </>
  ),
  plus: <path d="M10 4v12M4 10h12" />,
  minus: <path d="M4 10h12" />,
  keyboard: (
    <>
      <rect x="2" y="5" width="16" height="10" rx="1.5" />
      <path d="M5 8h1M8 8h1M11 8h1M14 8h1M5 11h1M14 11h1M8 11h4" />
    </>
  ),
  book: (
    <>
      <path d="M10 5.5C8.5 4 6 3.5 3 3.5v12c3 0 5.5.5 7 2 1.5-1.5 4-2 7-2v-12c-3 0-5.5.5-7 2z" />
      <path d="M10 5.5v12" />
    </>
  ),
  exit: (
    <>
      <path d="M8 3.5H4.5v13H8" />
      <path d="M8 10h9M13.5 6.5L17 10l-3.5 3.5" />
    </>
  ),
  white: (
    <>
      <path d="M5 17.5V3" />
      <path d="M5 3.5h10v7H5" />
    </>
  ),
  speaker: (
    <>
      <path d="M3.5 7.5h3l4-3.5v12l-4-3.5h-3z" />
      <path d="M13.5 7a4 4 0 0 1 0 6M15.5 5a7 7 0 0 1 0 10" />
    </>
  ),
  hourglass: (
    <>
      <path d="M5.5 3h9M5.5 17h9" />
      <path d="M6.5 3c0 4 7 4 7 7s-7 3-7 7M13.5 3c0 4-7 4-7 7s7 3 7 7" />
    </>
  ),
  swords: (
    <>
      <path d="M3.5 3.5l9 9M16.5 3.5l-9 9" />
      <path d="M11 14l3 3M9 14l-3 3M14.5 12l-2.5 2.5M5.5 12l2.5 2.5" />
    </>
  ),
  download: (
    <>
      <path d="M10 3.5v9M6.5 9l3.5 3.5L13.5 9" />
      <path d="M4 13.5v3h12v-3" />
    </>
  ),
  /** A reel of film (the film archive). */
  reel: (
    <>
      <circle cx="10" cy="10" r="6.5" />
      <circle cx="10" cy="10" r="1.2" />
      <circle cx="10" cy="6.2" r="1.3" />
      <circle cx="13.3" cy="11.9" r="1.3" />
      <circle cx="6.7" cy="11.9" r="1.3" />
      <path d="M16.5 10v6.5h1.5" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof PATHS;

export function Icon({
  name,
  size = 16,
  className,
  style,
  title,
}: {
  name: IconName;
  size?: number;
  className?: string;
  style?: CSSProperties;
  title?: string;
}) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 20 20',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className,
    style,
  } as const;
  // Decorative by default (the surrounding control carries the label); titled icons are images.
  if (title)
    return (
      <svg {...common} role="img">
        <title>{title}</title>
        {PATHS[name]}
      </svg>
    );
  return (
    <svg {...common} aria-hidden="true">
      {PATHS[name]}
    </svg>
  );
}
